/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Badge, Flex, Text } from '@radix-ui/themes';

import { SEVERITIES, Severity } from './quarantine.types';

import './SeverityBadge.scss';

export const SEVERITY_LABELS: Record<Severity, string> = {
  CRITICAL: 'Critical',
  SEVERE: 'Severe',
  MODERATE: 'Moderate',
  LOW: 'Low',
  NONE: 'None',
};

export const SEVERITY_COLORS: Record<Severity, 'red' | 'orange' | 'yellow' | 'gray'> = {
  CRITICAL: 'red',
  SEVERE: 'orange',
  MODERATE: 'yellow',
  LOW: 'gray',
  NONE: 'gray',
};

export function normalizeSeverity(value: string): Severity {
  return (SEVERITIES as string[]).includes(value) ? (value as Severity) : 'NONE';
}

export interface SeverityBadgeProps {
  /** IQ's 0-10 policy-severity score */
  threatLevel: number;
  /** Firewall severity band as sent by the backend; unknown values show as None */
  severity: string;
}

/** Colored score pill with the Firewall severity band label alongside. */
export function SeverityBadge({ threatLevel, severity }: SeverityBadgeProps): JSX.Element {
  const band = normalizeSeverity(severity);
  const label = SEVERITY_LABELS[band];
  const scoreText = Number.isInteger(threatLevel) ? String(threatLevel) : threatLevel.toFixed(1);

  return (
    <Flex
      align="center"
      gap="2"
      display="inline-flex"
      className="severity-badge"
      role="img"
      aria-label={`Threat level ${scoreText} - ${label}`}
    >
      <Badge
        size="1"
        variant="solid"
        radius="full"
        color={SEVERITY_COLORS[band]}
        className={`severity-badge__pill severity-badge__pill--${band.toLowerCase()}`}
        aria-hidden="true"
      >
        {scoreText}
      </Badge>
      <Text size="2" aria-hidden="true">
        {label}
      </Text>
    </Flex>
  );
}

export default SeverityBadge;
